import { faHouse } from '@fortawesome/pro-regular-svg-icons';
import { faDog } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QuantityPill } from 'components/AddonsMode/QuantityPill/QuantityPill';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { AddressCard } from 'components/OnlineBooking/Walking/Address/AddressCard';
import { AddressDialog } from 'components/OnlineBooking/Walking/Address/AddressDialog';
import { AddressPrompt } from 'components/OnlineBooking/Walking/Address/AddressPrompt';
import { OutOfAreaNotice } from 'components/OnlineBooking/Walking/Address/OutOfAreaNotice';
import { NoAvailability } from 'components/OnlineBooking/Steps/SelectStaff/NoAvailability/NoAvailability';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { Alert } from 'components/UI/Alert/Alert';
import { PromptModal } from 'components/UI/PromptModal/PromptModal';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { getHasInHomeByArea, getServices } from 'reducers/services/ServicesSelectors';
import { dispatch } from 'store';
import { CustomerError } from 'actions/customer/CustomerErrors';
import { AddonService } from 'types/AddonService';
import { CustomerAddress } from 'types/Customer';
import { Service } from 'types/Service';
import {
    fetchWalkingEligibilityThunk,
    saveWalkingCustomerAddressThunk
} from 'actions/onlineBooking/actions/OnlineBookingWalkingActions';
import { buildWalkingEligibilityRequestKey } from 'actions/onlineBooking/actions/OnlineBookingWalkingRequests';
import {
    navigateToStep,
    removeOnlineBookingBookingItems,
    updateOnlineBookingBookingItem,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingCustomer,
    getOnlineBookingWalkingAddressId,
    ONLINE_BOOKING_SESSION_SELECTORS
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { isOnlineBookingItemEmpty, OnlineBookingStep, SessionStepProps } from 'types/OnlineBooking';
import {
    clearWalkingItemDateTime,
    clearWalkingItemStaffSelection,
    deriveWalkingServiceListView,
    joinWalkingEligibleServices,
    resolveWalkingServiceConfig,
    SESSION_AREA_COPY
} from 'types/Walking';
import { createDefaultSchedule } from 'utils/addonSchedule';
import { getBusinessArea, trackServiceSelected } from 'utils/analytics';
import { getOnlineBookingAddonUnits } from 'utils/onlineBookingAddons';
import { formatServicePriceLabel, getServicePriceByPet } from 'utils/serviceUtils';
import classes from './OnlineBookingWalkingServiceListStep.module.scss';

export const OnlineBookingWalkingServiceListStep: React.FC<SessionStepProps> = React.memo(props => {
    const { businessArea } = props;
    const marketplace = useMarketplace();
    const sessionSelectors = ONLINE_BOOKING_SESSION_SELECTORS[businessArea];
    const copy = SESSION_AREA_COPY[businessArea];

    const services = useSelector(getServices);
    const walkingInHome = useSelector(getHasInHomeByArea[businessArea]);
    const customer = useSelector(getOnlineBookingCustomer);
    const walkingItems = useSelector(sessionSelectors.items);
    const addressId = useSelector(getOnlineBookingWalkingAddressId);
    const eligibilityCache = useSelector(sessionSelectors.eligibility);
    const addonServices = filterAddonsByBusinessAreas(useSelector(getActiveAddonServices), [
        businessArea
    ]);

    const [showExitConfirmationModal, setShowExitConfirmationModal] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [savingAddress, setSavingAddress] = React.useState(false);
    const [addressError, setAddressError] = React.useState<string>();
    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        walkingItems[0]?.uuid ?? ''
    );

    const numberOfPets = walkingItems.length;
    const requestKey = buildWalkingEligibilityRequestKey(marketplace.id, addressId, numberOfPets);
    const eligibilityState = eligibilityCache[requestKey];
    const eligibility = eligibilityState?.data;
    const fetchError = eligibilityState?.error;
    const isLoading = !eligibility && !fetchError;

    React.useEffect(() => {
        dispatch(fetchWalkingEligibilityThunk(businessArea, addressId, numberOfPets)).catch(
            () => undefined
        );
    }, [businessArea, addressId, numberOfPets]);

    React.useEffect(() => {
        if (eligibility?.reason) {
            console.debug(`${copy.label} eligibility reason`, eligibility.reason);
        }
    }, [copy.label, eligibility?.reason]);

    React.useEffect(() => {
        if (addressId == null) {
            return;
        }

        walkingItems.forEach(item => {
            if (item.addressId !== addressId) {
                dispatch(updateOnlineBookingBookingItem(item.uuid, { addressId }));
            }
        });
    }, [addressId, walkingItems]);

    const selectedBookingItem = React.useMemo(
        () => walkingItems.find(item => item.uuid === selectedBookingItemUuid),
        [walkingItems, selectedBookingItemUuid]
    );

    const visibleServices = React.useMemo(
        () => joinWalkingEligibleServices(eligibility?.services ?? [], services, numberOfPets),
        [eligibility?.services, services, numberOfPets]
    );

    const view = deriveWalkingServiceListView(
        {
            walkingInHome,
            addressId,
            inHomeOutOfArea: eligibility?.inHomeOutOfArea ?? false,
            inHomeExcludedReason: eligibility?.inHomeExcludedReason ?? null,
            servicesLength: eligibility?.services.length ?? 0,
            visibleServicesLength: visibleServices.length
        },
        businessArea
    );

    const currentAddress = customer.addresses?.find(address => address.id === addressId);

    const submitButtonLabel = React.useMemo(() => {
        const isLastPet =
            walkingItems.findIndex(item => item.uuid === selectedBookingItemUuid) ===
            walkingItems.length - 1;
        const hasPetsWithoutService = walkingItems
            .filter(item => item.uuid !== selectedBookingItemUuid)
            .some(item => !item.service?.id);

        if (hasPetsWithoutService || !isLastPet) {
            return 'Next Pet';
        }

        return 'Continue';
    }, [walkingItems, selectedBookingItemUuid]);

    const isValid = React.useMemo(() => {
        if (!selectedBookingItem?.service?.id) {
            return false;
        }

        const hasPetsWithoutService = walkingItems
            .filter(item => item.uuid !== selectedBookingItemUuid)
            .some(item => !item.service?.id);

        if (hasPetsWithoutService) {
            return true;
        }

        return walkingItems.every(item => item.service?.id);
    }, [walkingItems, selectedBookingItem, selectedBookingItemUuid]);

    const footerDisabled =
        isLoading || Boolean(fetchError) || visibleServices.length === 0 || !isValid;

    const serviceChangeHandler = (service: Service) => {
        if (!selectedBookingItem) {
            return;
        }

        dispatch(
            updateOnlineBookingBookingItems(
                walkingItems.map(item =>
                    item.uuid === selectedBookingItemUuid
                        ? {
                              ...clearWalkingItemDateTime(clearWalkingItemStaffSelection(item)),
                              service,
                              addressId
                          }
                        : clearWalkingItemDateTime(clearWalkingItemStaffSelection(item))
                )
            )
        );
    };

    // A walking or training Add-On has no days, only a quantity, kept as an Everyday schedule.
    const addonChangeHandler = (addon: AddonService, checked: boolean) => {
        const current = selectedBookingItem?.addons || [];
        const addonSchedules = { ...selectedBookingItem?.addonSchedules };
        const addons = checked
            ? [...current, addon]
            : current.filter(addonItem => addonItem.id !== addon.id);

        if (checked) {
            addonSchedules[addon.id] = createDefaultSchedule();
        } else {
            delete addonSchedules[addon.id];
        }

        dispatch(
            updateOnlineBookingBookingItem(selectedBookingItemUuid, { addons, addonSchedules })
        );
    };

    const addonQuantityChangeHandler = (addon: AddonService, quantity: number) => {
        dispatch(
            updateOnlineBookingBookingItem(selectedBookingItemUuid, {
                addonSchedules: {
                    ...selectedBookingItem?.addonSchedules,
                    [addon.id]: { ...createDefaultSchedule(), quantity }
                }
            })
        );
    };

    const backHandler = React.useCallback(() => {
        const emptyBookingItems = walkingItems.filter(isOnlineBookingItemEmpty);

        if (emptyBookingItems.length === walkingItems.length) {
            dispatch(removeOnlineBookingBookingItems(walkingItems.map(item => item.uuid)));

            return dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
        }

        setShowExitConfirmationModal(true);
    }, [walkingItems]);

    const exitConfirmationModalConfirmHandler = React.useCallback(() => {
        dispatch(removeOnlineBookingBookingItems(walkingItems.map(item => item.uuid)));
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [walkingItems]);

    const trackServiceSelection = () => {
        trackServiceSelected({
            marketplace_slug: marketplace.slug,
            business_area: getBusinessArea(walkingItems),
            service_names: walkingItems
                .map(item => item.service?.name)
                .filter(Boolean)
                .join(', '),
            pets_count: walkingItems.length
        });
    };

    const submitHandler = () => {
        const isLastPet =
            walkingItems.findIndex(item => item.uuid === selectedBookingItemUuid) ===
            walkingItems.length - 1;
        const petsWithoutService = walkingItems.filter(item => !item.service?.id);
        const otherPetsWithoutService = petsWithoutService.filter(
            item => item.uuid !== selectedBookingItemUuid
        );

        if (otherPetsWithoutService.length > 0 || !isLastPet) {
            const currentIndex = walkingItems.findIndex(
                item => item.uuid === selectedBookingItemUuid
            );
            const nextIndex = (currentIndex + 1) % walkingItems.length;
            const nextItem = walkingItems[nextIndex];
            const petChip = document.getElementById(nextItem.pet.uuid);

            petChip?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            setSelectedBookingItemUuid(nextItem.uuid);
            return;
        }

        trackServiceSelection();

        if (!marketplace.onlineBookingPreferences.allowSelectStaff) {
            return dispatch(navigateToStep(OnlineBookingStep.DatePicker));
        }

        dispatch(navigateToStep(OnlineBookingStep.StaffList));
    };

    const openAddressDialog = () => {
        setAddressError(undefined);
        setDialogOpen(true);
    };

    const saveAddressHandler = async (address: CustomerAddress) => {
        setSavingAddress(true);
        setAddressError(undefined);

        try {
            await dispatch(saveWalkingCustomerAddressThunk(address, businessArea));
            setDialogOpen(false);
        } catch (error) {
            const message =
                error instanceof CustomerError ? error.message : 'Oops, something went wrong.';

            setAddressError(message);
        } finally {
            setSavingAddress(false);
        }
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                {view.showPromptBanner && <AddressPrompt onAddAddress={openAddressDialog} />}

                {view.showAddressCard && currentAddress && (
                    <AddressCard address={currentAddress} onChangeAddress={openAddressDialog} />
                )}

                {view.showOutOfAreaNotice && (
                    <OutOfAreaNotice onChangeAddress={openAddressDialog} />
                )}

                <div className={classes.petTabs}>
                    {walkingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            checked={!!item.service?.id}
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                {isLoading && (
                    <div className={classes.loading}>
                        <Spinner size={50} />
                    </div>
                )}

                {fetchError && !isLoading && (
                    <Alert
                        variant="error"
                        icon={faInfoCircle}
                        text="Something went wrong. Please go back and try again."
                    />
                )}

                {!isLoading && !fetchError && eligibility && view.noServicesCopy && (
                    <NoAvailability icon={faDog} description={view.noServicesCopy} />
                )}

                {!isLoading && !fetchError && eligibility && view.showList && (
                    <>
                        <h1 className="h3 w-full">{copy.label} Services</h1>

                        {visibleServices.length > 0 && selectedBookingItem && (
                            <div className="flex flex-col gap-3">
                                {visibleServices.map(({ service }) => {
                                    const price = getServicePriceByPet(
                                        service,
                                        selectedBookingItem.pet
                                    );
                                    const { inHomeService } = resolveWalkingServiceConfig(service);

                                    return (
                                        <RadioCard
                                            key={service.id}
                                            title={service.name}
                                            badge={
                                                inHomeService ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faHouse} /> Home
                                                        Service
                                                    </>
                                                ) : undefined
                                            }
                                            badgeClassName={
                                                inHomeService ? classes.homeServiceBadge : undefined
                                            }
                                            description={formatServicePriceLabel(
                                                price,
                                                marketplace.onlineBookingPreferences.pricingOption
                                            )}
                                            checked={selectedBookingItem.service?.id === service.id}
                                            content={service.description}
                                            onChange={() => serviceChangeHandler(service)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {addonServices.length > 0 &&
                    visibleServices.length > 0 &&
                    !isLoading &&
                    !fetchError && (
                        <>
                            <h1 className="h3 w-full">{copy.label} Add-ons</h1>

                            <div className="flex flex-col gap-3">
                                {addonServices.map(addon => (
                                    <RadioCard
                                        key={addon.id}
                                        mode="checkbox"
                                        title={addon.name}
                                        checked={Boolean(
                                            selectedBookingItem?.addons.some(
                                                item => item.id === addon.id
                                            )
                                        )}
                                        description={formatServicePriceLabel(
                                            addon.price,
                                            marketplace.onlineBookingPreferences.pricingOption
                                        )}
                                        onChange={checked => addonChangeHandler(addon, checked)}
                                        checkedAction={
                                            selectedBookingItem && (
                                                <QuantityPill
                                                    label={`${addon.name} quantity`}
                                                    value={getOnlineBookingAddonUnits(
                                                        selectedBookingItem,
                                                        addon
                                                    )}
                                                    min={0}
                                                    onChange={quantity =>
                                                        quantity === 0
                                                            ? addonChangeHandler(addon, false)
                                                            : addonQuantityChangeHandler(
                                                                  addon,
                                                                  quantity
                                                              )
                                                    }
                                                />
                                            )
                                        }
                                    />
                                ))}
                            </div>
                        </>
                    )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label={submitButtonLabel}
                        disabled={footerDisabled}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>

            {showExitConfirmationModal && (
                <PromptModal
                    open={true}
                    title={`Exit ${copy.label} Services`}
                    description="Closing now will discard any unsaved changes. Do you want to exit?"
                    onConfirm={exitConfirmationModalConfirmHandler}
                    onCancel={() => setShowExitConfirmationModal(false)}
                />
            )}

            <AddressDialog
                open={dialogOpen}
                editing={Boolean(currentAddress)}
                saving={savingAddress}
                error={addressError}
                onClose={() => setDialogOpen(false)}
                onDismissError={() => setAddressError(undefined)}
                onSave={saveAddressHandler}
            />
        </TwoColumnsLayout>
    );
});

OnlineBookingWalkingServiceListStep.displayName = 'OnlineBookingWalkingServiceListStep';
